import React, { useState } from "react";
import { Button } from "@/components/Base/Button";
import { Input } from "@/components/Base/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Icon } from "@/components/Common/Icon";
import { IconNames, ActivityType, TerminologyOptions } from "@/types/v2";
import { ActivityFeedSearchFilters } from "@/hooks/useActivityFeedPagination";
import { DatePicker } from "@/components/Base/DatePicker";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { addDays } from "date-fns";

interface ActivityFeedSearchControlsProps {
  filters: ActivityFeedSearchFilters;
  onFiltersChange: (filters: ActivityFeedSearchFilters) => void;
  onReset: () => void;
  loading?: boolean;
}

const activityTypeOptions = [
  { value: "all", label: "All Activities" },
  { value: ActivityType.CONTAINER_MOVEMENT, label: "Container Movement" },
  { value: ActivityType.NOTE_ADDED, label: "Note Added" },
  { value: ActivityType.NOTE_UPDATED, label: "Note Updated" },
  { value: ActivityType.NOTE_DELETED, label: "Note Deleted" },
  { value: ActivityType.TIMER_STARTED, label: "Timer Started" },
  { value: ActivityType.TIMER_PAUSED, label: "Timer Paused" },
  { value: ActivityType.TIMER_RESET, label: "Timer Reset" },
  { value: ActivityType.TIMER_RESTARTED, label: "Timer Restarted" },
  {
    value: ActivityType.TIMER_RESET_WHILE_RUNNING,
    label: "Timer Reset (Running)",
  },
  { value: ActivityType.TIMER_CHANGED, label: "Timer Changed" },
  { value: ActivityType.TAG_ADDED, label: "Tag Added" },
  { value: ActivityType.TAG_REMOVED, label: "Tag Removed" },
  {
    value: ActivityType.CUSTOMER_UPDATE_CREATED,
    label: "Customer Update Created",
  },
  {
    value: ActivityType.CUSTOMER_UPDATE_SCHEDULED,
    label: "Customer Update Scheduled",
  },
  {
    value: ActivityType.CUSTOMER_UPDATE_COMPLETED,
    label: "Customer Update Completed",
  },
  {
    value: ActivityType.CUSTOMER_UPDATE_CANCELLED,
    label: "Customer Update Cancelled",
  },
  {
    value: ActivityType.CUSTOMER_UPDATE_SNOOZED,
    label: "Customer Update Snoozed",
  },
  {
    value: ActivityType.CUSTOMER_UPDATE_SKIPPED,
    label: "Customer Update Skipped",
  },
];

export const ActivityFeedSearchControls: React.FC<
  ActivityFeedSearchControlsProps
> = ({ filters, onFiltersChange, onReset, loading = false }) => {
  const { terminolize } = useTerminologyReplacer();
  const [localFilters, setLocalFilters] =
    useState<ActivityFeedSearchFilters>(filters);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const handleFilterChange = (
    key: keyof ActivityFeedSearchFilters,
    value: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    setIsFiltersOpen(false);
  };

  const hasActiveFilters = Object.keys(filters).some(
    (key) => filters[key as keyof ActivityFeedSearchFilters] !== undefined
  );

  const hasPendingFilters = Object.keys(localFilters).some(
    (key) =>
      localFilters[key as keyof ActivityFeedSearchFilters] !==
      filters[key as keyof ActivityFeedSearchFilters]
  );

  const handleReset = () => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    if (hasActiveFilters) onReset();
    setIsFiltersOpen(false);
  };

  // Is the current filters state different from the applied filters?
  const isDifferentFromApplied =
    JSON.stringify(localFilters) !== JSON.stringify(filters);

  const getFilterSummary = () => {
    const summaryParts: string[] = [];

    if (filters.activityType) {
      const typeLabel =
        activityTypeOptions.find((opt) => opt.value === filters.activityType)
          ?.label || filters.activityType;
      summaryParts.push(typeLabel);
    }

    if (filters.cardNumber) {
      summaryParts.push(
        `${terminolize("RO", TerminologyOptions.repairOrderNumber, "acronym")}: ${filters.cardNumber}`
      );
    }

    if (filters.startDate && filters.endDate) {
      summaryParts.push(
        `${filters.startDate.toLocaleString()} to ${addDays(filters.endDate, 1).toLocaleString()}`
      );
    } else if (filters.startDate) {
      summaryParts.push(`From ${filters.startDate.toLocaleString()}`);
    } else if (filters.endDate) {
      summaryParts.push(
        `Until ${addDays(filters.endDate, 1).toLocaleString()}`
      );
    }

    return summaryParts.join(", ");
  };

  return (
    <div className="flex flex-col px-4 pb-3 pt-3">
      {/* Header — title left, actions right, no icon/container (matches other side panels) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold leading-none text-foreground">
            Activity Feed
          </h3>
          {hasActiveFilters && !isFiltersOpen && (
            <div className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
              {getFilterSummary()}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && !isFiltersOpen && (
            <Button
              onClick={handleReset}
              variant="ghost"
              size="sm"
              disabled={loading}
              className="h-7 px-2 text-xs"
            >
              Clear
            </Button>
          )}
          <Button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            variant="outline"
            size="sm"
            className="h-7 px-3 text-xs"
          >
            <Icon
              name={isFiltersOpen ? IconNames.chevronUp : IconNames.filters}
              className="mr-1 h-3 w-3"
            />
            Filters
          </Button>
        </div>
      </div>

      {/* Collapsible Filters Area */}
      {isFiltersOpen && (
        <div className="mt-4 space-y-4 text-xs">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Activity Type Filter */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Activity Type
              </label>
              <Select
                value={localFilters.activityType || "all"}
                onValueChange={(value) =>
                  handleFilterChange(
                    "activityType",
                    value === "all" ? undefined : value
                  )
                }
                disabled={loading}
              >
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="All Activities" />
                </SelectTrigger>
                <SelectContent>
                  {activityTypeOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className={"text-xs"}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Card Number Filter */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                {terminolize(
                  "RO Number",
                  TerminologyOptions.repairOrderNumber,
                  "acronym"
                )}
              </label>
              <Input
                placeholder={`Enter ${terminolize(
                  "RO Number",
                  TerminologyOptions.repairOrderNumber,
                  "acronym"
                )}...`}
                value={localFilters.cardNumber || ""}
                onChange={(e) =>
                  handleFilterChange("cardNumber", e.target.value || undefined)
                }
                disabled={loading}
                className="text-xs"
              />
            </div>

            {/* Start Date Filter */}
            <div className="space-y-2">
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  Start Date
                </label>
              </div>

              <DatePicker
                className={"w-full text-xs"}
                value={localFilters.startDate}
                onChange={(value) => {
                  handleFilterChange("startDate", value);
                  if (
                    value &&
                    localFilters.endDate &&
                    value > localFilters.endDate
                  ) {
                    handleFilterChange("endDate", undefined);
                  }
                }}
                displayFormat={"yyyy-MM-dd"}
                disabled={loading}
                showClear={localFilters.startDate !== undefined}
              />
            </div>

            {/* End Date Filter */}
            <div className="space-y-2">
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  End Date
                </label>
              </div>

              <DatePicker
                className={"w-full text-xs"}
                value={localFilters.endDate}
                onChange={(value) => {
                  handleFilterChange("endDate", value);
                  if (
                    value &&
                    localFilters.startDate &&
                    value < localFilters.startDate
                  ) {
                    handleFilterChange("startDate", undefined);
                  }
                }}
                displayFormat={"yyyy-MM-dd"}
                disabled={loading}
                showClear={localFilters.endDate !== undefined}
              />
            </div>
          </div>

          <div className="flex gap-2">
            {isDifferentFromApplied && (
              <Button
                onClick={handleApplyFilters}
                loading={loading}
                size="sm"
                className="flex items-center gap-2 text-xs"
              >
                <Icon name={IconNames.search} className="h-4 w-4" />
                Apply Filters
              </Button>
            )}

            {(hasActiveFilters || hasPendingFilters) && (
              <Button
                onClick={handleReset}
                variant="outline"
                size="sm"
                disabled={loading}
                className="flex items-center gap-2 text-xs"
              >
                <Icon name={IconNames.clearFilters} className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
